// Factory functions for creating common filter patterns

import type { FilterConfig, FilterOption, QueryMapping } from './types';

/**
 * Factory for creating keyword/text search filters
 */
export function createKeywordFilter(config: {
	label: string;
	placeholder: string;
	objectTable: string;
	listKeys: Array<string>;
	fulltextFields?: Array<string>;
	fulltextIndex?: string;
	supportsRelevance?: boolean;
}): FilterConfig {
	const queryMapping: QueryMapping = {
		objectTable: config.objectTable,
		joinType: 'direct',
	};

	if (config.fulltextFields != null) {
		queryMapping.fulltextFields = config.fulltextFields;
	}

	if (config.fulltextIndex != null) {
		queryMapping.fulltextIndex = config.fulltextIndex;
	}

	// Auto-set supportsRelevance only when fulltextIndex (OpenSearch) is used.
	// fulltextFields alone uses LIKE pattern matching which doesn't support relevance.
	const supportsRelevance =
		config.supportsRelevance ?? config.fulltextIndex != null;

	return {
		type: 'text',
		label: config.label,
		placeholder: config.placeholder,
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			primary: 'value' as keyof unknown,
		},
		queryParamName: '',
		multiple: false,
		queryMapping,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: config.listKeys,
		supportsRelevance,
	};
}

/**
 * Factory for creating date range filters
 */
export function createDateRangeFilter(config: {
	label: string;
	objectTable: string;
	dateField: string;
	listKeys: Array<string>;
	placeholder?: string;
	/**
	 * Optional raw SQL expression used in place of `${tableAlias}.${dateField}`
	 * for the comparison. Lets a single date filter target a fallback chain
	 * like `COALESCE(item.externalCreatedDate, DATE(item._created_date))`.
	 * The string is interpolated into SQL — must be code-authored, never
	 * derived from user input.
	 */
	dateExpression?: string;
	/**
	 * Optional SQL alias override when the date column lives on a JOINED table
	 * rather than the base entity (see QueryMapping.columnAlias).
	 */
	columnAlias?: string;
}): FilterConfig {
	return {
		type: 'date-range',
		label: config.label,
		placeholder: config.placeholder ?? 'Select date range...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: '',
		multiple: false,
		queryMapping: {
			objectTable: config.objectTable,
			dateField: config.dateField,
			joinType: 'direct',
			...(config.dateExpression != null
				? { dateExpression: config.dateExpression }
				: {}),
			...(config.columnAlias != null
				? { columnAlias: config.columnAlias }
				: {}),
		},
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		listKeys: config.listKeys,
	};
}

/**
 * Factory for creating status/multi-select filters with static options
 */
export function createStatusFilter(config: {
	label: string;
	options: Array<FilterOption>;
	objectTable: string;
	fieldName: string;
	listKeys: Array<string>;
	placeholder?: string;
	colorScheme?:
		| 'blue'
		| 'green'
		| 'purple'
		| 'gray'
		| 'red'
		| 'yellow'
		| 'orange';
	/**
	 * Optional SQL alias override when the filtered column lives on a JOINED
	 * table rather than the base entity (see QueryMapping.columnAlias).
	 */
	columnAlias?: string;
}): FilterConfig {
	return {
		type: 'multi-select',
		label: config.label,
		placeholder:
			config.placeholder ?? `Select ${config.label.toLowerCase()}...`,
		colorScheme: config.colorScheme ?? 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: '',
		multiple: true,
		options: config.options,
		queryMapping: {
			objectTable: config.objectTable,
			fieldName: config.fieldName,
			joinType: 'direct',
			...(config.columnAlias != null
				? { columnAlias: config.columnAlias }
				: {}),
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: config.listKeys,
	};
}

/**
 * Factory for creating radio/single-select filters with static options
 */
export function createRadioFilter(config: {
	label: string;
	options: Array<FilterOption>;
	defaultValue?: FilterOption;
	objectTable: string;
	fieldName: string;
	listKeys: Array<string>;
	colorScheme?:
		| 'blue'
		| 'green'
		| 'purple'
		| 'gray'
		| 'red'
		| 'yellow'
		| 'orange';
}): FilterConfig {
	return {
		type: 'radio',
		label: config.label,
		colorScheme: config.colorScheme ?? 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: '',
		multiple: false,
		options: config.options,
		defaultValue: config.defaultValue,
		queryMapping: {
			objectTable: config.objectTable,
			fieldName: config.fieldName,
			joinType: 'direct',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: config.listKeys,
	};
}

/**
 * Factory for creating deleted records filters (soft-delete handling)
 * Creates entity-specific filter like 'clientDeletedRecords', 'itemDeletedRecords', etc.
 */
export function createDeletedRecordsFilter(config: {
	objectTable: string;
	listKey: string;
}): FilterConfig {
	return {
		type: 'radio',
		label: 'Deleted Records',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: `${config.listKey}DeletedRecords`,
		multiple: false,
		options: [
			{ id: 'active', label: 'Active Only' },
			{ id: 'all', label: 'All Records' },
			{ id: 'deleted', label: 'Deleted Only' },
		],
		defaultValue: { id: 'active', label: 'Active Only' },
		queryMapping: {
			objectTable: config.objectTable,
			fieldName: '_deleted_date',
			joinType: 'soft-delete',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [config.listKey],
		hideCount: true,
	};
}

/**
 * Factory for the archived-records radio (PD-15452), modeled on the
 * deleted-records filter above. Archived clients are hidden from lists by
 * default; this filter surfaces them. The "show everything" option id is
 * deliberately `any`, NOT `all` — the frontend FilterService drops radio
 * values whose id is the literal `all` ("All = no filter" convention), and
 * for this filter an absent value means "hide archived", not "everything".
 */
export function createArchivedRecordsFilter(config: {
	objectTable: string;
	listKey: string;
}): FilterConfig {
	return {
		type: 'radio',
		label: 'Archived Records',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: `${config.listKey}ArchivedRecords`,
		multiple: false,
		options: [
			{ id: 'active', label: 'Hide Archived' },
			{ id: 'any', label: 'All Records' },
			{ id: 'archived', label: 'Archived Only' },
		],
		defaultValue: { id: 'active', label: 'Hide Archived' },
		queryMapping: {
			objectTable: config.objectTable,
			fieldName: 'archivedDate',
			joinType: 'archived',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [config.listKey],
		hideCount: true,
	};
}

/**
 * Factory for creating entity lookup filters (with API endpoint)
 */
export function createEntityLookupFilter(config: {
	label: string;
	placeholder: string;
	apiEndpoint: string;
	objectTable: string;
	fieldName: string;
	listKeys: Array<string>;
	displayFields: {
		id: string;
		primary: string;
		secondary?: string;
	};
	colorScheme?:
		| 'blue'
		| 'green'
		| 'purple'
		| 'gray'
		| 'red'
		| 'yellow'
		| 'orange';
	multiple?: boolean;
	searchable?: boolean;
	joinType?: QueryMapping['joinType'];
	joinTable?: string;
	joinConditions?: QueryMapping['joinConditions'];
}): FilterConfig {
	const queryMapping: QueryMapping = {
		objectTable: config.objectTable,
		fieldName: config.fieldName,
		joinType: config.joinType ?? 'direct',
	};

	if (config.joinTable != null) {
		queryMapping.joinTable = config.joinTable;
	}

	if (config.joinConditions != null) {
		queryMapping.joinConditions = config.joinConditions;
	}

	return {
		type: 'multi-select',
		label: config.label,
		placeholder: config.placeholder,
		colorScheme: config.colorScheme ?? 'purple',
		searchable: config.searchable ?? true,
		loadOnFocus: true,
		allowClear: true,
		displayFields:
			config.displayFields as unknown as FilterConfig['displayFields'],
		apiEndpoint: config.apiEndpoint,
		queryParamName: '',
		multiple: config.multiple ?? true,
		queryMapping,
		format: config.multiple === false ? 'single' : 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: config.listKeys,
	};
}
